import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import '../Home/Home.scss'
import '../HomeDetails/HomeDetails.scss'
import './Sales.scss'
import MobileHeader from '../Common/Header/MobileHeader'
import Header from '../Common/Header/Header'
import Config from '../../config/config'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import SideMenu from '../Common/SideMenu/SideMenu'
import BarcodeIcon from '../../assets/BarcodeIcon'
import BarcodeScanner from '../../components/ZXingScanner/BarcodeScanner'
import TrashIcon from '../../assets/TrashIcon'
import PopupCard from '../Common/PopupCard/PopupCard'
import {
  createSalesHelper,
  updateSalesHelper,
  getSalesOrderHelper,
  searchCustomersHelper,
  getAllCustomersHelper,
  getProductDetailsByBarcodeHelper,
  getBranchProductsHelper,
  cancelSalesHelper,
  confirmOnlineSalesOrderHelper,
  updatePrescriptionOrderHelper,
} from '../../helper/home-helper'
import { getAllPOSHelper } from '../../helper/pos-details-helper'
import { getAllMFSHelper } from '../../helper/mfs-details-helper'
import {
  getProductSearchSuggestionsHelper,
  getProductDetailByIdHelper,
} from '../../helper/personal-details-helper'
import {
  createSalesAlertAction,
  holdSalesAlertAction,
  updateSalesAlertAction,
  activeHomeDetailNavigation,
} from '../../modules/actions/action'
import {
  CUSTOMER_FIELDS,
  PRODUCT_DETAIL_FIELDS,
  PAYMENT_DETAILS_FIELDS,
  PAYMENT_MODE_OPTIONS,
  PERMISSION_FEATURE,
  PERMISSION_TYPE,
  HOME_NAVIGATION_ITEMS,
  HOME_CONST,
  SALES_FLOW,
  SALES_TYPE,
} from '../../common/constant/page-constant'
import { SIDEBAR_CONST } from '../../common/constant/common-constant'
import { formatDate } from '../../common/utils/appUtils'
import { formatProductListLabel } from '../../common/utils/productUtils'
import { saveDraft, loadDraft, clearDraft } from '../../common/utils/storageUtils'
import DRAFT_KEYS from '../../draft/Draft'
import { handlePrintInvoice } from './printHandler'

const SALES_COUNTRY_CODE_MAX_LEN = 4
const SALES_LOCAL_PHONE_MAX_LEN = 11

const KNOWN_DIAL_CODES = ['+88', '+1', '+44', '+91', '+86']

const clampSalesCountryCode = (v) => String(v ?? '').slice(0, SALES_COUNTRY_CODE_MAX_LEN)

const clampSalesLocalPhoneDigits = (v) =>
  String(v ?? '')
    .replace(/\D/g, '')
    .slice(0, SALES_LOCAL_PHONE_MAX_LEN)

const splitDialCodeFromPhone = (fullContact, defaultCode = '+88') => {
  if (fullContact == null || fullContact === '') {
    return { countryCode: defaultCode, phoneNumber: '' }
  }
  const s = String(fullContact)
  for (const code of KNOWN_DIAL_CODES) {
    if (s.startsWith(code)) {
      return {
        countryCode: code,
        phoneNumber: clampSalesLocalPhoneDigits(s.substring(code.length)),
      }
    }
  }
  return {
    countryCode: defaultCode,
    phoneNumber: clampSalesLocalPhoneDigits(s),
  }
}

// FEFO: soonest valid expiry among in-stock lines (fallback: any line if none in stock)
const SALES_PRODUCT_SELECT_INSTANCE_ID = 'sales-product-select'

// Sellable qty for a batch. Backends with the hold soft-reservation change send
// available_quantity (quantity - hold_quantity); older ones only send stock.
const availQty = (b) => parseInt(b?.available_quantity ?? b?.stock) || 0

const pickNearestExpiryBatch = (inventory) => {
  if (!Array.isArray(inventory) || inventory.length === 0) return null
  const inStock = inventory.filter((b) => availQty(b) > 0)
  const list = (inStock.length > 0 ? inStock : [...inventory]).slice()
  const expiryTime = (exp) => {
    const t = new Date(exp).getTime()
    return Number.isFinite(t) ? t : Infinity
  }
  list.sort((a, b) => expiryTime(a.expiry_date) - expiryTime(b.expiry_date))
  return list[0] || null
}

// Display helper: render 0/empty/null as an empty input so the field doesn't
// show a leading "0" the user has to delete before typing. Strings are passed
// through untouched so user-typed "0" stays visible long enough to complete
// a decimal entry like "0.5".
const numInput = (v) => {
  if (v === undefined || v === null || v === '') return ''
  if (typeof v === 'number' && v === 0) return ''
  return v
}

// Strip leading zeros from a raw input string (preserves "0.x" decimals).
const stripLeadingZeros = (s) => {
  if (s === '' || s == null) return ''
  const str = String(s)
  if (str.startsWith('-')) return '-' + stripLeadingZeros(str.slice(1))
  return str.replace(/^0+(?=\d)/, '')
}

// TEMP-DISABLE-PAYMODE: payment modes temporarily disabled in the Pay Mode
// dropdown. Empty this array (or delete it and the isOptionDisabled prop +
// the `option` style block below that reference it) to re-enable them all at once.
const TEMP_DISABLED_PAY_MODES = ['CARD', 'ONLINE', 'CASH_CARD']

// Returns the row's effective discount/VAT/total.
// Per-row discountAmount is always the source of truth — bill-level discount
// handlers distribute back into each row before this is called, so no
// override logic is needed here.
const getEffectiveLine = (row) => {
  const qty = parseFloat(row.quantity) || 0
  const rate = parseFloat(row.ratePerUnit) || 0
  const base = qty * rate

  const effDiscAmt = parseFloat(row.discountAmount) || 0
  const effDiscPct =
    base > 0 ? parseFloat(((effDiscAmt / base) * 100).toFixed(2)) : 0
  const effTotal = parseFloat((base - effDiscAmt).toFixed(2))

  // VAT is CPU-based and product-type-driven (informational only).
  //   Pharma     → 0% / 0 BDT.
  //   Non-pharma → 7.5% fixed, amount = qty × CPU × 0.075.
  const isPharma =
    String(row.productType || '').toUpperCase() === 'PHARMACEUTICAL'
  const cpu = parseFloat(row.costPerUnit) || 0
  const effVatPct = isPharma ? 0 : 7.5
  const effVatAmt = isPharma
    ? 0
    : parseFloat((qty * cpu * 0.075).toFixed(2))

  return {
    discountPercentage: effDiscPct,
    discountAmount: effDiscAmt,
    vatPercentage: effVatPct,
    vatAmount: effVatAmt,
    totalAmount: effTotal,
  }
}

// FEFO allocation: consume nearest-expiry batches first up to requestedQty.
// Returns { allocations: [{batch, qty}], shortfall, totalAvailable }.
const allocateAcrossBatches = (inventory, requestedQty) => {
  const totalAvailable = Array.isArray(inventory)
    ? inventory.reduce((sum, b) => sum + availQty(b), 0)
    : 0
  if (!Array.isArray(inventory) || requestedQty <= 0) {
    return { allocations: [], shortfall: requestedQty, totalAvailable }
  }
  const expiryTime = (exp) => {
    const t = new Date(exp).getTime()
    return Number.isFinite(t) ? t : Infinity
  }
  const sorted = inventory
    .filter((b) => availQty(b) > 0)
    .slice()
    .sort((a, b) => expiryTime(a.expiry_date) - expiryTime(b.expiry_date))
  const allocations = []
  let remaining = requestedQty
  for (const batch of sorted) {
    if (remaining <= 0) break
    const take = Math.min(remaining, availQty(batch))
    if (take > 0) {
      allocations.push({ batch, qty: take })
      remaining -= take
    }
  }
  return { allocations, shortfall: remaining, totalAvailable }
}

const SalesForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const isMobile = useCheckMobileScreen()
  const loginData = useSelector((state) => state.loginDataReducer?.loginData)
  const permissions = loginData?.permissions

  const isEditMode = id && id !== 'new'
  
  // Use different draft keys for new mode vs edit mode (per sales order ID)
  // Using SALES_FORM key to avoid conflict with Sales.js drawer component
  const DRAFT_KEY = isEditMode 
    ? `${DRAFT_KEYS.SALES_FORM}_edit_${id}` 
    : DRAFT_KEYS.SALES_FORM
  
  // Track if initial data load is complete (for edit mode) or draft restore is complete (for new mode)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  // Section 1: Customer Details
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerCountryCode, setCustomerCountryCode] = useState('+88')
  const [customerName, setCustomerName] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customerSuggestions, setCustomerSuggestions] = useState([])
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [branchCustomerOptions, setBranchCustomerOptions] = useState([])

  // Section 2: Product Details
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [productBarcode, setProductBarcode] = useState('')
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [quantity, setQuantity] = useState('')
  const [costPerUnit, setCostPerUnit] = useState(0)
  const [ratePerUnit, setRatePerUnit] = useState(0)
  const [discountPercentage, setDiscountPercentage] = useState(0)
  const [discountAmount, setDiscountAmount] = useState(0)
  // Tracks whether the user last typed into Disc % or Disc Amt. On add, if the
  // amount was typed we preserve it exactly and derive the percentage, so the
  // 2-decimal rounding on Disc % can't drift the typed amount by a cent.
  const [lastDiscountField, setLastDiscountField] = useState('percentage')
  const [vatPercentage, setVatPercentage] = useState(0)
  const [vatAmount, setVatAmount] = useState(0)
  const [productSearchSuggestions, setProductSearchSuggestions] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [isScannerVisible, setIsScannerVisible] = useState(false)

  // Section 3: Products List
  const [productsInTable, setProductsInTable] = useState([])
  // Visual highlight only — set on single-click. Doesn't enable edit or delete.
  const [selectedTableRowIndex, setSelectedTableRowIndex] = useState(null)
  // Set only by double-click. Drives the form (populated with row data),
  // the Add/Update button label, the Delete button/key, and the FEFO
  // cascade's "skip this row" logic.
  const [editingRowIndex, setEditingRowIndex] = useState(null)

  // Section 4: Bill Summary
  const [totalQty, setTotalQty] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [tableDiscountPercentage, setTableDiscountPercentage] = useState(0)
  const [tableDiscountAmount, setTableDiscountAmount] = useState(0)
  const [subTotal, setSubTotal] = useState(0)
  const [totalVAT, setTotalVAT] = useState(0)
  const [netAmount, setNetAmount] = useState(0)
  const [paidAmount, setPaidAmount] = useState('')
  const [returnAmount, setReturnAmount] = useState(0)

  // Section 5: Payment Details
  const [paymentMode, setPaymentMode] = useState('CASH')
  const [cashAmount, setCashAmount] = useState(0)
  const [cardAmount, setCardAmount] = useState(0)
  const [notes, setNotes] = useState('')

  // POS and MFS data for payment modes
  const [posList, setPosList] = useState([])
  const [mfsList, setMfsList] = useState([])
  const [selectedPOS, setSelectedPOS] = useState(null)
  const [selectedCardType, setSelectedCardType] = useState(null) // SAME_BANK or OTHER_BANK
  const [selectedMFS, setSelectedMFS] = useState(null)
  const [transactionFeePercent, setTransactionFeePercent] = useState(0)
  const [transactionFeeAmount, setTransactionFeeAmount] = useState(0)
  const [totalPayable, setTotalPayable] = useState(0)

  // Card type options
  const CARD_TYPE_OPTIONS = [
    { name: 'SAME_BANK', label: 'Same Bank' },
    { name: 'OTHER_BANK', label: 'Other Bank' }
  ]

  // Print checkbox - default to checked
  const [shouldPrint, setShouldPrint] = useState(true)

  // UI State
  const [loading, setLoading] = useState(false)
  const [serverError, setServerError] = useState(undefined)
  const [isFieldErrorVisible, setIsFieldErrorVisible] = useState(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [deleteProductPopup, setDeleteProductPopup] = useState(false)
  const [salesFlow, setSalesFlow] = useState(SALES_FLOW.OFFLINE)
  const [validationError, setValidationError] = useState(null) // For popup validation errors

  // Online order state
  const [isOnlineOrder, setIsOnlineOrder] = useState(false)
  const [onlineOrderStatus, setOnlineOrderStatus] = useState(null)
  // Status of the loaded order in edit mode ('HOLD' / 'COMPLETED' / etc.).
  // Drives the "Complete Sale" button visibility and the Update flow's payload.
  const [currentOrderStatus, setCurrentOrderStatus] = useState(null)
  const [isPrescriptionOrder, setIsPrescriptionOrder] = useState(false)
  const [prescriptionFiles, setPrescriptionFiles] = useState([])
  const [showConfirmPopup, setShowConfirmPopup] = useState(false)
  const [showCancelOrderPopup, setShowCancelOrderPopup] = useState(false)
  const [selectedPrescriptionImage, setSelectedPrescriptionImage] = useState(null)

  // Refs for keyboard navigation (Enter key to move to next field)
  const barcodeInputRef = useRef(null)
  const customerCountryCodeRef = useRef(null)
  const customerPhoneRef = useRef(null)
  const customerNameSelectWrapRef = useRef(null)
  const customerAddressRef = useRef(null)
  const productSelectWrapRef = useRef(null)
  const batchSelectWrapRef = useRef(null)
  const quantityRef = useRef(null)
  const discountPercentageRef = useRef(null)
  const discountAmountRef = useRef(null)
  const tableDiscountPercentageRef = useRef(null)
  const tableDiscountAmountRef = useRef(null)
  // Set to true by bill-level discount handlers so the bill summary useEffect
  // doesn't overwrite the user-typed bill fields with the product-sum recompute.
  const billDiscountOriginRef = useRef(false)
  const paidAmountRef = useRef(null)
  const productSearchTimerRef = useRef(null)
  const productSearchAbortRef = useRef(null)
  const productSelectMenuOpenRef = useRef(false)
  const batchSelectMenuOpenRef = useRef(false)
  const customerNameSelectMenuOpenRef = useRef(false)
  const runProductEnterNavRef = useRef(() => {})

  // Load all products on mount and focus first input
  useEffect(() => {
    loadAllProducts()
    loadPOSData()
    loadMFSData()
    getAllCustomersHelper()
      .then((customers) => {
        const opts = (customers || []).map((c) => ({
          value: c.id,
          label: c.name || '',
          id: c.id,
          name: c.name,
          mobile_no: c.mobile_no,
          address: c.billing_address || c.shipping_address || '',
          billing_address: c.billing_address,
          shipping_address: c.shipping_address,
        }))
        setBranchCustomerOptions(opts)
      })
      .catch(() => {
        setBranchCustomerOptions([])
      })
    if (customerCountryCodeRef.current) {
      customerCountryCodeRef.current.focus()
    }
  }, [])

  // Load POS data
  const loadPOSData = () => {
    getAllPOSHelper()
      .then(res => {
        setPosList(res || [])
      })
      .catch(err => {
        console.log('Error loading POS data: ', err)
        setPosList([])
      })
  }

  // Load MFS data
  const loadMFSData = () => {
    getAllMFSHelper()
      .then(res => {
        setMfsList(res || [])
      })
      .catch(err => {
        console.log('Error loading MFS data: ', err)
        setMfsList([])
      })
  }

  // Load edit data
  useEffect(() => {
    if (isEditMode) {
      loadSalesOrder(id)
    }
  }, [id])

  // Close customer suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-field-wrapper')) {
        setShowCustomerSuggestions(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Locked when an existing customer is attached, so a new sale can't silently
  // edit that customer's master profile — EXCEPT when resuming a hold order,
  // where customer details must stay editable whether or not they were provided.
  const isCustomerDetailsLocked =
    Boolean(selectedCustomer?.id) &&
    !(isEditMode && currentOrderStatus === 'HOLD')

  useEffect(() => {
    if (isCustomerDetailsLocked) {
      setShowCustomerSuggestions(false)
      setSelectedSuggestionIndex(-1)
    }
  }, [isCustomerDetailsLocked])

  // Helper function to restore draft data
  const restoreDraftData = (draft) => {
    // Section 1: Customer Details
    if (draft.customerPhone) setCustomerPhone(clampSalesLocalPhoneDigits(draft.customerPhone))
    if (draft.customerCountryCode) setCustomerCountryCode(clampSalesCountryCode(draft.customerCountryCode))
    if (draft.customerName) setCustomerName(draft.customerName)
    if (draft.customerAddress) setCustomerAddress(draft.customerAddress)
    if (draft.selectedCustomer) setSelectedCustomer(draft.selectedCustomer)
    // Section 2: Product Details (form fields)
    if (draft.selectedProduct) setSelectedProduct(draft.selectedProduct)
    if (draft.productBarcode) setProductBarcode(draft.productBarcode)
    if (draft.selectedBatch) setSelectedBatch(draft.selectedBatch)
    if (draft.quantity) setQuantity(draft.quantity)
    if (draft.costPerUnit !== undefined) setCostPerUnit(draft.costPerUnit)
    if (draft.ratePerUnit !== undefined) setRatePerUnit(draft.ratePerUnit)
    if (draft.discountPercentage !== undefined) setDiscountPercentage(draft.discountPercentage)
    if (draft.discountAmount !== undefined) setDiscountAmount(draft.discountAmount)
    if (draft.vatPercentage !== undefined) setVatPercentage(draft.vatPercentage)
    if (draft.vatAmount !== undefined) setVatAmount(draft.vatAmount)
    // Section 3: Products Table
    if (draft.productsInTable) setProductsInTable(draft.productsInTable)
    // Section 4: Bill Summary
    if (draft.tableDiscountPercentage !== undefined) setTableDiscountPercentage(draft.tableDiscountPercentage)
    if (draft.tableDiscountAmount !== undefined) setTableDiscountAmount(draft.tableDiscountAmount)
    if (draft.paidAmount) setPaidAmount(draft.paidAmount)
    // Section 5: Payment Details
    if (draft.paymentMode) setPaymentMode(draft.paymentMode)
    if (draft.cashAmount !== undefined) setCashAmount(draft.cashAmount)
    if (draft.cardAmount !== undefined) setCardAmount(draft.cardAmount)
    if (draft.notes) setNotes(draft.notes)
    if (draft.selectedPOS) setSelectedPOS(draft.selectedPOS)
    if (draft.selectedCardType) setSelectedCardType(draft.selectedCardType)
    if (draft.selectedMFS) setSelectedMFS(draft.selectedMFS)
    // Print checkbox
    if (draft.shouldPrint !== undefined) setShouldPrint(draft.shouldPrint)
  }

  // Restore draft on mount (for new mode)
  useEffect(() => {
    if (!isEditMode) {
      const draft = loadDraft(DRAFT_KEY)
      if (draft) {
        restoreDraftData(draft)
      }
      // Mark initial load/restore as complete so draft saving can begin
      setIsInitialLoadComplete(true)
    }
  }, [isEditMode])

  // Persist draft on state change (for both new and edit mode)
  // Only save after initial load/restore is complete to avoid overwriting with empty data
  useEffect(() => {
    if (isInitialLoadComplete) {
      saveDraft(DRAFT_KEY, {
        // Section 1: Customer Details
        customerPhone,
        customerCountryCode,
        customerName,
        customerAddress,
        selectedCustomer,
        // Section 2: Product Details (form fields)
        selectedProduct,
        productBarcode,
        selectedBatch,
        quantity,
        costPerUnit,
        ratePerUnit,
        discountPercentage,
        discountAmount,
        vatPercentage,
        vatAmount,
        // Section 3: Products Table
        productsInTable,
        // Section 4: Bill Summary
        tableDiscountPercentage,
        tableDiscountAmount,
        paidAmount,
        // Section 5: Payment Details
        paymentMode,
        cashAmount,
        cardAmount,
        notes,
        selectedPOS,
        selectedCardType,
        selectedMFS,
        // Print checkbox
        shouldPrint
      })
    }
  }, [
    isEditMode,
    isInitialLoadComplete,
    customerPhone,
    customerCountryCode,
    customerName,
    customerAddress,
    selectedCustomer,
    selectedProduct,
    productBarcode,
    selectedBatch,
    quantity,
    costPerUnit,
    ratePerUnit,
    discountPercentage,
    discountAmount,
    vatPercentage,
    vatAmount,
    productsInTable,
    tableDiscountPercentage,
    tableDiscountAmount,
    paidAmount,
    paymentMode,
    cashAmount,
    cardAmount,
    notes,
    selectedPOS,
    selectedCardType,
    selectedMFS,
    shouldPrint
  ])

  const loadAllProducts = async () => {
    getBranchProductsHelper()
      .then((products) => {
        if (products && Array.isArray(products)) {
          setAllProducts(products)
          setProductSearchSuggestions(products)
        }
      })
      .catch((err) => {
        console.log('Error loading products:', err)
      })
  }

  const loadSalesOrder = async (orderId) => {
    setLoading(true)
    getSalesOrderHelper(orderId)
      .then((order) => {
        // Populate customer details
        setCustomerName(order.customer?.name || '')
        const loadedMobile = splitDialCodeFromPhone(order.customer?.mobile_no)
        setCustomerCountryCode(clampSalesCountryCode(loadedMobile.countryCode))
        setCustomerPhone(loadedMobile.phoneNumber)
        setCustomerAddress(
          order.customer?.shipping_address ||
            order.customer?.billing_address ||
            ''
        )
        setSelectedCustomer(order.customer)

        // Populate products table
        if (order.product_details && order.product_details.length > 0) {
          const products = order.product_details.map((detail, index) => {
            const productType = String(
              detail.product_type ||
                detail.productDetails?.type ||
                detail.type ||
                'PHARMACEUTICAL'
            )
              .toUpperCase()
              .replace(/ /g, '_')
            const isPharma = productType === 'PHARMACEUTICAL'
            const qty = detail[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0
            const cpu =
              detail.inventory?.cost_per_unit ||
              detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]
                ?.cost_per_unit ||
              0
            // VAT policy is fixed: pharma 0 / non-pharma 7.5%. Any value stored
            // on the loaded row is ignored here so the policy is enforced.
            const policyVatPct = isPharma ? 0 : 7.5
            const policyVatAmt = isPharma
              ? 0
              : parseFloat((qty * cpu * 0.075).toFixed(2))
            const loadedRate = detail[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0
            const loadedDiscPct = parseFloat(detail.discount_percentage) || 0
            const loadedDiscAmt = parseFloat(
              ((qty * loadedRate * loadedDiscPct) / 100).toFixed(2)
            )
            return {
              id: detail.id || `product_${index}`,
              productDetails: detail,
              batchDetails:
                detail.inventory || detail.selected_batch_details || {},
              barcode: detail.inventory?.barcode || '',
              productName: detail[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || '',
              dose: detail[PRODUCT_DETAIL_FIELDS.DOSE] || '',
              strength: detail.strength || '',
              size: detail.size || '',
              category: detail.category || '',
              productType,
              batchNumber:
                detail.inventory?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER] ||
                detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
                  PRODUCT_DETAIL_FIELDS.BATCH_NUMBER
                ] ||
                '',
              expiryDate:
                detail.inventory?.expiry_date ||
                detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]
                  ?.expiry_date ||
                '',
              quantity: qty,
              costPerUnit: cpu,
              ratePerUnit: loadedRate,
              discountPercentage: loadedDiscPct,
              discountAmount: loadedDiscAmt,
              vatPercentage: policyVatPct,
              vatAmount: policyVatAmt,
              totalAmount: detail.final_price || 0,
              rxMode: detail.inventory?.is_prescription_required || detail.is_prescription_required ? 'Rx' : 'OTC',
            }
          })

          // Bill-level totals are derived by the bill summary useEffect from
          // the per-product discounts loaded above — no manual distribution needed.
          setProductsInTable(products)
        }

        console.log('rojon order', order)

        // Populate payment details
        setPaymentMode(order.payment_mode || 'CASH')
        setNotes(order.notes || '')

        if (order.payment_mode === 'CASH_CARD') {
          setCashAmount(order.amount_cash || 0)
          setCardAmount(order.amount_card || 0)
        }

        setSalesFlow(
          order.type === 'IN_STORE' ? SALES_FLOW.OFFLINE : SALES_FLOW.ONLINE
        )

        // Set online order state
        const isOnline = order.type === 'ONLINE'
        setIsOnlineOrder(isOnline)
        setOnlineOrderStatus(order.status)
        setCurrentOrderStatus(order.status)
        
        if (isOnline) {
          // Check if it's a prescription order
          const isPrescription = order.order_via_prescription || 
            order.status === 'PRESCRIPTION_PENDING_ON_STORE'
          setIsPrescriptionOrder(isPrescription)
          
          // Load prescription files if available
          if (order.files && order.files.length > 0) {
            setPrescriptionFiles(order.files)
          }
        }
        
        // After loading from backend, check if there's a draft with unsaved changes
        // The draft has priority because it contains more recent user edits
        const editDraftKey = `${DRAFT_KEYS.SALES_FORM}_edit_${orderId}`
        const draft = loadDraft(editDraftKey)
        if (draft) {
          // Restore draft data (overwrites backend data with user's unsaved changes)
          restoreDraftData(draft)
        }
        
        // Mark initial load as complete so draft saving can begin
        setIsInitialLoadComplete(true)
        setLoading(false)
      })
      .catch((err) => {
        console.log('Error loading sales order:', err)
        setIsInitialLoadComplete(true) // Allow saving even if load fails
        setLoading(false)
      })
  }

  // Customer search by phone
  const handleCustomerPhoneChange = (phone) => {
    const digits = clampSalesLocalPhoneDigits(phone)
    setCustomerPhone(digits)
    setSelectedSuggestionIndex(-1)

    if (digits.length >= 3) {
      searchCustomersHelper(digits)
        .then((customers) => {
          if (customers && customers.length > 0) {
            setCustomerSuggestions(customers)
            setShowCustomerSuggestions(true)

            if (customers.length === 1) {
              const customer = customers[0]
              const parsed = splitDialCodeFromPhone(customer.mobile_no)
              setCustomerName(customer.name || '')
              setCustomerCountryCode(clampSalesCountryCode(parsed.countryCode))
              setCustomerAddress(
                customer.address || customer.shipping_address || ''
              )
              setSelectedCustomer(customer)
            }
          } else {
            setCustomerSuggestions([])
            setShowCustomerSuggestions(false)
          }
        })
        .catch(() => {
          setCustomerSuggestions([])
          setShowCustomerSuggestions(false)
        })
    } else {
      setCustomerSuggestions([])
      setShowCustomerSuggestions(false)
    }
  }

  // Handle keyboard navigation for customer suggestions
  const handleCustomerInputKeyDown = (e) => {
    if (!showCustomerSuggestions || customerSuggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedSuggestionIndex((prevIndex) =>
        prevIndex < customerSuggestions.length - 1 ? prevIndex + 1 : 0
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedSuggestionIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : customerSuggestions.length - 1
      )
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault()
      handleSelectCustomer(customerSuggestions[selectedSuggestionIndex])
    } else if (e.key === 'Escape') {
      setShowCustomerSuggestions(false)
      setSelectedSuggestionIndex(-1)
    }
  }

  const focusFieldRef = (ref) => {
    const node = ref?.current
    if (node == null) return false
    if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
      if (node.readOnly || node.disabled) return false
      node.focus()
      return true
    }
    if (node instanceof HTMLSelectElement) {
      if (node.disabled) return false
      node.focus()
      return true
    }
    const rsControl =
      typeof node.querySelector === 'function'
        ? node.querySelector('.react-select__control') ||
          node.querySelector('[role="combobox"]')
        : null
    if (rsControl instanceof HTMLElement) {
      if (rsControl.getAttribute('aria-disabled') === 'true') return false
      rsControl.focus()
      return true
    }
    if (typeof node.focus === 'function' && node instanceof HTMLElement) {
      node.focus()
      return (
        document.activeElement === node ||
        (document.activeElement != null &&
          node.contains(document.activeElement))
      )
    }
    return false
  }

  const focusFirstSkippableRef = (nextRefOrRefs) => {
    const refs = Array.isArray(nextRefOrRefs) ? nextRefOrRefs : [nextRefOrRefs]
    for (let i = 0; i < refs.length; i++) {
      if (focusFieldRef(refs[i])) return
    }
  }

  runProductEnterNavRef.current = () => {
    productSelectMenuOpenRef.current = false
    focusFirstSkippableRef([batchSelectWrapRef, quantityRef])
  }

  const isSalesProductDropdownMenuOpen = (wrapEl) => {
    const expandEl =
      wrapEl.querySelector?.('.react-select__control') ||
      wrapEl.querySelector?.('[role="combobox"]')
    if (expandEl?.getAttribute('aria-expanded') === 'true') return true
    const menuId = expandEl?.getAttribute?.('aria-controls')
    if (menuId) {
      const menuNode = document.getElementById(menuId)
      if (
        menuNode &&
        menuNode.getAttribute('role') === 'listbox' &&
        menuNode.offsetParent !== null &&
        menuNode.getAttribute('aria-hidden') !== 'true'
      ) {
        return true
      }
    }
    const filterInput =
      wrapEl.querySelector?.('.react-select__input-container input') ||
      wrapEl.querySelector?.('input')
    if (filterInput?.getAttribute?.('aria-activedescendant')) return true
    const listbox = document.querySelector(
      `[id*="${SALES_PRODUCT_SELECT_INSTANCE_ID}"][id$="listbox"], [id*="${SALES_PRODUCT_SELECT_INSTANCE_ID}-listbox"]`
    )
    if (
      listbox &&
      listbox.offsetParent !== null &&
      listbox.getAttribute('aria-hidden') !== 'true'
    ) {
      return true
    }
    return false
  }

  // Native capture: let react-select handle Enter when the menu is open (arrow + Enter to pick).
  useLayoutEffect(() => {
    const el = productSelectWrapRef.current
    if (!el) return undefined
    const handler = (e) => {
      if (e.key !== 'Enter') return
      if (isSalesProductDropdownMenuOpen(el)) return
      e.preventDefault()
      e.stopPropagation()
      runProductEnterNavRef.current()
    }
    el.addEventListener('keydown', handler, true)
    return () => el.removeEventListener('keydown', handler, true)
  }, [])

  // Handle Enter key navigation — move to next field, skipping read-only/disabled inputs
  const handleFieldKeyDown = (e, nextRefOrRefs) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (nextRefOrRefs == null) return
      focusFirstSkippableRef(nextRefOrRefs)
    }
  }

  // Handle Enter key for Select dropdowns: first Enter selects from menu; when menu closed, move to next field
  const handleSelectKeyDown = (e, nextRefOrRefs, menuIsOpen) => {
    if (e.key === 'Enter' && !menuIsOpen) {
      e.preventDefault()
      e.stopPropagation()
      focusFirstSkippableRef(nextRefOrRefs)
    }
  }

  const handleCustomerPhoneKeyDown = (e) => {
    handleCustomerInputKeyDown(e)
    if (e.defaultPrevented) return
    handleFieldKeyDown(e, [
      customerNameSelectWrapRef,
      customerAddressRef,
      barcodeInputRef,
    ])
  }

  const handleSelectCustomer = (customer) => {
    const parsed = splitDialCodeFromPhone(customer.mobile_no)
    setCustomerName(customer.name || '')
    setCustomerCountryCode(clampSalesCountryCode(parsed.countryCode))
    setCustomerPhone(parsed.phoneNumber)
    setCustomerAddress(customer.address || customer.shipping_address || '')
    setSelectedCustomer(customer)
    setShowCustomerSuggestions(false)
  }

  const handleCustomerNameSelectChange = (selectedOption, actionMeta) => {
    setSelectedSuggestionIndex(-1)
    setShowCustomerSuggestions(false)

    if (actionMeta.action === 'select-option' && selectedOption) {
      setCustomerName(selectedOption.name || selectedOption.label || '')
      const parsed = splitDialCodeFromPhone(selectedOption.mobile_no)
      setCustomerCountryCode(clampSalesCountryCode(parsed.countryCode))
      setCustomerPhone(parsed.phoneNumber)
      setCustomerAddress(
        selectedOption.address ||
          selectedOption.billing_address ||
          selectedOption.shipping_address ||
          ''
      )
      setSelectedCustomer({
        id: selectedOption.id,
        name: selectedOption.name,
        mobile_no: selectedOption.mobile_no,
        billing_address: selectedOption.billing_address,
        shipping_address: selectedOption.shipping_address,
        address: selectedOption.address,
      })
      customerNameSelectMenuOpenRef.current = false
      setTimeout(() => {
        focusFirstSkippableRef([customerAddressRef, barcodeInputRef])
      }, 0)
      return
    }

    if (actionMeta.action === 'create-option' && selectedOption) {
      const label =
        typeof selectedOption.label === 'string'
          ? selectedOption.label
          : String(selectedOption.label || '').trim()
      setCustomerName(label)
      setSelectedCustomer(null)
      customerNameSelectMenuOpenRef.current = false
      setTimeout(() => {
        focusFirstSkippableRef([customerAddressRef, barcodeInputRef])
      }, 0)
      return
    }

    if (actionMeta.action === 'clear') {
      customerNameSelectMenuOpenRef.current = false
      handleClearCustomer()
    }
  }

  // Product search by barcode
  const getProductDetailsByBarcode = (barcode, options) => {
    const focusAfterLookup = options?.focusAfter === true
    if (!barcode) {
      setServerError(undefined)
      return
    }

    getProductDetailsByBarcodeHelper(barcode)
      .then((product) => {
        if (product) {
          setServerError(undefined)
          setSelectedProduct(product)
          setProductBarcode(product.barcode || barcode)
          setDiscountPercentage(product.discount_percentage || 0)

          if (product.inventory && product.inventory.length > 0) {
            const batch = pickNearestExpiryBatch(product.inventory)
            setSelectedBatch(batch)
            if (batch) {
              setCostPerUnit(batch.cost_per_unit || 0)
              setRatePerUnit(batch.rate_per_unit || 0)
              // VAT is derived from product type by the VAT useEffect;
              // do not read batch.vat anymore.
            }
          } else {
            setSelectedBatch(null)
          }
          productSelectMenuOpenRef.current = false
          batchSelectMenuOpenRef.current = false
          if (focusAfterLookup) {
            setTimeout(() => {
              focusFirstSkippableRef([batchSelectWrapRef, quantityRef])
            }, 0)
          }
        }
      })
      .catch((err) => {
        setServerError(err || 'Product not found')
        setTimeout(() => setServerError(undefined), 5000)
      })
  }

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      getProductDetailsByBarcode(productBarcode, { focusAfter: true })
    }
  }

  const handleProductSearch = (searchTerm) => {
    if (productSearchAbortRef.current) {
      productSearchAbortRef.current.abort()
    }
    if (productSearchTimerRef.current) {
      clearTimeout(productSearchTimerRef.current)
    }

    if (!searchTerm) {
      setProductSearchSuggestions(allProducts)
      return
    }

    productSearchTimerRef.current = setTimeout(() => {
      const abortController = new AbortController()
      productSearchAbortRef.current = abortController

      getProductSearchSuggestionsHelper(searchTerm, abortController.signal)
        .then((suggestions) => {
          if (abortController.signal.aborted) return
          setProductSearchSuggestions(suggestions || [])
        })
        .catch(() => {
          if (!abortController.signal.aborted) {
            setProductSearchSuggestions(allProducts)
          }
        })
    }, 500)
  }

  const handleProductSelect = (option) => {
    // If user clicked X to clear, reset product fields
    if (!option) {
      handleClearProduct()
      return
    }
    if (!option.id) return

    getProductDetailByIdHelper(option.id)
      .then((product) => {
        if (product) {
          setServerError(undefined)
          setSelectedProduct(product)
          setProductBarcode(product.barcode || '')
          setDiscountPercentage(product.discount_percentage || 0)
          productSelectMenuOpenRef.current = false
          batchSelectMenuOpenRef.current = false

          if (product.inventory && product.inventory.length > 0) {
            const batch = pickNearestExpiryBatch(product.inventory)
            setSelectedBatch(batch)
            if (batch) {
              setCostPerUnit(batch.cost_per_unit || 0)
              setRatePerUnit(batch.rate_per_unit || 0)
              // VAT is derived from product type by the VAT useEffect;
              // do not read batch.vat anymore.
            }
          } else {
            setSelectedBatch(null)
          }
        }
      })
      .catch((err) => {
        setServerError('Product not found')
        setTimeout(() => setServerError(undefined), 5000)
      })
  }

  // Barcode scanner
  const handleScan = (result) => {
    if (result) {
      setProductBarcode(result)
      getProductDetailsByBarcode(result, { focusAfter: true })
      setIsScannerVisible(false)
    }
  }

  // Update CPU, RPU and VAT when batch changes
  useEffect(() => {
    if (selectedBatch) {
      setCostPerUnit(selectedBatch.cost_per_unit || 0)
      setRatePerUnit(selectedBatch.rate_per_unit || 0)
      setVatPercentage(selectedBatch.vat || 0)
    }
  }, [selectedBatch])

  // Product-level discount calculations.
  // Only recompute Disc Amt from % when qty or rate changes AND the user last
  // typed in the % field. When lastDiscountField is 'amount' (amount was typed
  // directly, or a row was loaded from the table), keep the stored amount and
  // back-compute % instead — preserving the exact amount the user set.
  useEffect(() => {
    if (quantity && ratePerUnit) {
      const totalPrice = parseFloat(quantity) * parseFloat(ratePerUnit)
      if (lastDiscountField === 'percentage') {
        const discAmt = (totalPrice * parseFloat(discountPercentage || 0)) / 100
        setDiscountAmount(parseFloat(discAmt.toFixed(2)))
      } else {
        // amount is the source of truth; back-compute % so it stays in sync
        const amt = parseFloat(discountAmount) || 0
        const pct = totalPrice > 0 ? (amt / totalPrice) * 100 : 0
        setDiscountPercentage(parseFloat(pct.toFixed(4)))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, ratePerUnit])

  // Product-level VAT calculations.
  // New policy: VAT is product-type-driven and CPU-based, not MRP-based.
  //   Pharma     → 0% / 0 BDT
  //   Non-pharma → 7.5% fixed, VAT amount = qty × CPU × 0.075
  // VAT is informational only (not added to the customer-payable total),
  // so it is independent of discount and rate-per-unit.
  useEffect(() => {
    if (!selectedProduct) {
      setVatPercentage(0)
      setVatAmount(0)
      return
    }
    const isPharma =
      String(selectedProduct?.type || '').toUpperCase() === 'PHARMACEUTICAL'
    if (isPharma) {
      setVatPercentage(0)
      setVatAmount(0)
    } else {
      setVatPercentage(7.5)
      const qtyNum = parseFloat(quantity) || 0
      const cpuNum = parseFloat(costPerUnit) || 0
      setVatAmount(parseFloat((qtyNum * cpuNum * 0.075).toFixed(2)))
    }
  }, [selectedProduct, quantity, costPerUnit])

  // Add or Update product in table
  const handleAddOrUpdateProduct = () => {
    if (!selectedProduct || !quantity || !selectedBatch) {
      setIsFieldErrorVisible(true)
      return false
    }

    const requestedQty = parseInt(quantity)
    const productId = selectedProduct.product_id || selectedProduct.id

    // Subtract qty already used by other rows of the same product so the
    // cascade doesn't re-allocate stock that's already on the invoice.
    const usedByOtherRows = {}
    productsInTable.forEach((row, idx) => {
      if (editingRowIndex !== null && idx === editingRowIndex) return
      const rowProductId = row.product_id || row.productDetails?.product_id || row.productDetails?.id
      if (rowProductId !== productId) return
      const bid = row.product_batch_inventory_id
      if (!bid) return
      usedByOtherRows[bid] = (usedByOtherRows[bid] || 0) + (parseInt(row.quantity) || 0)
    })

    // Inventory should be an array of all available batches. If the row was
    // loaded from a saved hold order without a refresh, fall back to the
    // single batch currently selected so cascading and validation still work.
    const rawInventory = Array.isArray(selectedProduct.inventory)
      ? selectedProduct.inventory
      : selectedBatch
        ? [selectedBatch]
        : []
    const remainingInventory = rawInventory.map((b) => {
      const bid = b.product_batch_inventory_id || b.id
      const used = usedByOtherRows[bid] || 0
      // Subtract used quantities from available (not physical stock)
      return {
        ...b,
        available_quantity: Math.max(0, availQty(b) - used),
      }
    })

    const { allocations, shortfall, totalAvailable } = allocateAcrossBatches(
      remainingInventory,
      requestedQty,
    )

    if (shortfall > 0 || allocations.length === 0) {
      setServerError(
        `Quantity exceeds total available stock (${totalAvailable} units)`,
      )
      return false
    }

    const ratePerUnitNum = parseFloat(ratePerUnit) || 0
    const costPerUnitNum = parseFloat(costPerUnit) || 0
    const discountPctNum = parseFloat(discountPercentage) || 0
    const vatPctNum = parseFloat(vatPercentage) || 0

    // Normalize product type once per submit so each row carries it for VAT.
    const rowProductType = String(selectedProduct?.type || 'PHARMACEUTICAL')
      .toUpperCase()
      .replace(/ /g, '_')
    const isPharma = rowProductType === 'PHARMACEUTICAL'

    // When the user typed Disc Amt, treat it as the source of truth and split
    // it across batch allocations by qty. Deriving the percentage from each
    // row's resulting amount avoids the 2-decimal rounding on Disc % drifting
    // the total by a cent (e.g. 50 on a 300 base would round-trip to 50.01).
    const amountDriven = lastDiscountField === 'amount'
    const typedDiscountAmount = parseFloat(discountAmount) || 0

    const buildRowFromAllocation = (allocation, idOverride) => {
      const allocQty = allocation.qty
      const lineTotal = allocQty * ratePerUnitNum
      const lineDiscount = amountDriven
        ? requestedQty > 0
          ? parseFloat(((allocQty / requestedQty) * typedDiscountAmount).toFixed(2))
          : 0
        : parseFloat(((lineTotal * discountPctNum) / 100).toFixed(2))
      const lineDiscountPct = amountDriven
        ? lineTotal > 0
          ? parseFloat(((lineDiscount / lineTotal) * 100).toFixed(2))
          : 0
        : discountPctNum
      // VAT is CPU-based, product-type-driven, informational only.
      const lineVatPct = isPharma ? 0 : 7.5
      const lineVat = isPharma
        ? 0
        : parseFloat((allocQty * costPerUnitNum * 0.075).toFixed(2))
      return {
        id: idOverride || `product_${Date.now()}_${allocation.batch.product_batch_inventory_id || allocation.batch.id}`,
        productDetails: selectedProduct,
        batchDetails: allocation.batch,
        barcode: productBarcode,
        productName: selectedProduct.name || selectedProduct.product_name,
        dose: selectedProduct.dose || '',
        strength: selectedProduct.strength || '',
        size: selectedProduct.size || '',
        category: selectedProduct.category || '',
        productType: rowProductType,
        batchNumber: allocation.batch.batch_number,
        expiryDate: allocation.batch.expiry_date,
        quantity: allocQty,
        costPerUnit: costPerUnitNum,
        ratePerUnit: ratePerUnitNum,
        discountPercentage: lineDiscountPct,
        discountAmount: lineDiscount,
        vatPercentage: lineVatPct,
        vatAmount: lineVat,
        totalAmount: parseFloat((lineTotal - lineDiscount).toFixed(2)),
        product_id: selectedProduct.product_id || selectedProduct.id,
        product_batch_inventory_id:
          allocation.batch.product_batch_inventory_id || allocation.batch.id,
        rxMode: allocation.batch?.is_prescription_required || selectedProduct?.is_prescription_required ? 'Rx' : 'OTC',
      }
    }

    const newRows = allocations.map((alloc, idx) =>
      buildRowFromAllocation(
        alloc,
        idx === 0 && editingRowIndex !== null
          ? productsInTable[editingRowIndex].id
          : undefined,
      ),
    )

    if (editingRowIndex !== null) {
      const updatedProducts = [...productsInTable]
      updatedProducts.splice(editingRowIndex, 1, ...newRows)
      setProductsInTable(updatedProducts)
      setEditingRowIndex(null)
      setSelectedTableRowIndex(null)
    } else {
      setProductsInTable([...productsInTable, ...newRows])
    }

    handleClearProduct()
    return true
  }

  const handleProductLastFieldKeyDown = (e) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    e.stopPropagation()
    if (handleAddOrUpdateProduct()) {
      setTimeout(() => {
        focusFirstSkippableRef([barcodeInputRef])
      }, 0)
    }
  }

  const handleSalesFormKeyDown = (e) => {
    if (e.key === 'Delete' && editingRowIndex !== null) {
      e.preventDefault()
      setDeleteProductPopup(true)
    }
  }

  const handleClearCustomer = () => {
    customerNameSelectMenuOpenRef.current = false
    setCustomerPhone('')
    setCustomerCountryCode('+88')
    setCustomerName('')
    setCustomerAddress('')
    setSelectedCustomer(null)
    setCustomerSuggestions([])
    setShowCustomerSuggestions(false)
  }

  const handleClearProduct = () => {
    productSelectMenuOpenRef.current = false
    batchSelectMenuOpenRef.current = false
    setSelectedProduct(null)
    setProductBarcode('')
    setSelectedBatch(null)
    setQuantity('')
    setCostPerUnit(0)
    setRatePerUnit(0)
    setDiscountPercentage(0)
    setDiscountAmount(0)
    setLastDiscountField('percentage')
    setVatPercentage(0)
    setVatAmount(0)
    setSelectedTableRowIndex(null)
    setEditingRowIndex(null)
    setIsFieldErrorVisible(false)
    setServerError(undefined)
  }

  const handleDeleteProduct = () => {
    if (editingRowIndex !== null) {
      const updatedProducts = [...productsInTable]
      updatedProducts.splice(editingRowIndex, 1)
      setProductsInTable(updatedProducts)
      setSelectedTableRowIndex(null)
      setEditingRowIndex(null)
      setDeleteProductPopup(false)
      handleClearProduct()
    }
  }

  // Single-click only selects the row visually (and enables Delete).
  // It does NOT change any form-edit state — the Add button stays "ADD",
  // and a subsequent Add will still append (not replace) a row.
  const handleTableRowClick = (index) => {
    setSelectedTableRowIndex(index)
  }

  // Double-click loads the row into the form for editing. We refresh the
  // product's full inventory from the backend so the FEFO cascade has the
  // complete batch list — rows loaded from a saved HOLD order only carry the
  // single batch they used, not the full inventory array.
  const handleTableRowDoubleClick = (index) => {
    const product = productsInTable[index]
    if (!product) return
    setSelectedTableRowIndex(index)
    setEditingRowIndex(index)
    setIsFieldErrorVisible(false)
    setServerError(undefined)

    setProductBarcode(product.barcode || '')
    setSelectedBatch(product.batchDetails)
    setQuantity(product.quantity.toString())
    setCostPerUnit(product.costPerUnit || 0)
    setRatePerUnit(product.ratePerUnit || 0)
    setDiscountPercentage(product.discountPercentage || 0)
    setDiscountAmount(product.discountAmount || 0)
    // Mark amount as source of truth so the product discount useEffect does
    // not zero it out when ratePerUnit is reset by the selectedBatch effect.
    setLastDiscountField('amount')
    setVatPercentage(product.vatPercentage || 0)
    setVatAmount(product.vatAmount || 0)

    const existingInventory = Array.isArray(product.productDetails?.inventory)
      ? product.productDetails.inventory
      : null

    // If the row's productDetails already carries a real inventory array
    // (i.e. it was added in this session via the product dropdown), reuse it.
    if (existingInventory) {
      setSelectedProduct(product.productDetails)
      return
    }

    // Otherwise refresh from the API. Fall back to a single-batch shim so the
    // cascade keeps working even if the call fails.
    const productId =
      product.product_id ||
      product.productDetails?.product_id ||
      product.productDetails?.id

    if (!productId) {
      setSelectedProduct({
        ...product.productDetails,
        inventory: product.batchDetails ? [product.batchDetails] : [],
      })
      return
    }

    getProductDetailByIdHelper(productId)
      .then((fresh) => {
        if (fresh && Array.isArray(fresh.inventory)) {
          setSelectedProduct(fresh)
        } else {
          setSelectedProduct({
            ...product.productDetails,
            inventory: product.batchDetails ? [product.batchDetails] : [],
          })
        }
      })
      .catch(() => {
        setSelectedProduct({
          ...product.productDetails,
          inventory: product.batchDetails ? [product.batchDetails] : [],
        })
      })
  }

  // Invoice summary calculations.
  // Bill-level discount fields (tableDiscountPercentage / tableDiscountAmount)
  // are derived from the product rows here when a product-level change triggers
  // this effect. When a bill-level discount handler fires first, it sets
  // billDiscountOriginRef so this effect skips overwriting those fields — the
  // handler already set them to exactly what the user typed.
  useEffect(() => {
    let qty = 0
    let base = 0
    let totalDisc = 0
    let totalVat = 0
    let totalNet = 0

    productsInTable.forEach((row) => {
      const eff = getEffectiveLine(row)
      qty += parseInt(row.quantity) || 0
      base +=
        (parseFloat(row.quantity) || 0) * (parseFloat(row.ratePerUnit) || 0)
      totalDisc += eff.discountAmount
      totalVat += eff.vatAmount
      totalNet += eff.totalAmount
    })

    setTotalQty(qty)
    setTotalItems(productsInTable.length)
    setTotalAmount(parseFloat(base.toFixed(2)))
    setTotalVAT(parseFloat(totalVat.toFixed(2)))
    setSubTotal(parseFloat(totalNet.toFixed(2)))
    setNetAmount(parseFloat(totalNet.toFixed(2)))

    if (billDiscountOriginRef.current) {
      // Bill-level handler already set discount fields; don't overwrite them.
      billDiscountOriginRef.current = false
    } else {
      // Product-level change: derive bill fields from the product sums.
      const discAmt = parseFloat(totalDisc.toFixed(2))
      const discPct = base > 0
        ? parseFloat(((totalDisc / base) * 100).toFixed(2))
        : 0
      setTableDiscountAmount(discAmt)
      setTableDiscountPercentage(discPct)
    }
  }, [productsInTable])

  // Calculate Return Amount based on payment mode and total payable
  useEffect(() => {
    let amountToPay = netAmount

    // For Card, Online, and Cash & Card - use total payable (includes transaction fee)
    if ((paymentMode === 'CARD' || paymentMode === 'ONLINE' || paymentMode === 'CASH_CARD') && transactionFeeAmount > 0) {
      amountToPay = totalPayable
    }

    const returnAmt = parseFloat(paidAmount || 0) - amountToPay
    setReturnAmount(parseFloat(returnAmt.toFixed(2)))
  }, [paidAmount, netAmount, paymentMode, transactionFeeAmount, totalPayable])

  // Bill-level discount handlers.
  // Both clear per-product discounts and redistribute proportionally so that
  // bill-level and product-level discounts never co-exist.
  const distributeDiscountToProducts = (pctNum, amtNum, base) => {
    billDiscountOriginRef.current = true
    setProductsInTable((prev) =>
      prev.map((row) => {
        const rowBase =
          (parseFloat(row.quantity) || 0) * (parseFloat(row.ratePerUnit) || 0)
        // Proportional share: row's weight in the total base.
        const rowDisc =
          base > 0
            ? parseFloat(((rowBase / base) * amtNum).toFixed(2))
            : 0
        const rowDiscPct =
          rowBase > 0
            ? parseFloat(((rowDisc / rowBase) * 100).toFixed(4))
            : 0
        return { ...row, discountAmount: rowDisc, discountPercentage: rowDiscPct }
      })
    )
  }

  const handleTableDiscountPercentageChange = (discountPercent) => {
    const base = parseFloat(totalAmount) || 0
    const pctNum = parseFloat(discountPercent) || 0
    const amtNum = parseFloat(((base * pctNum) / 100).toFixed(2))
    setTableDiscountPercentage(discountPercent)
    setTableDiscountAmount(amtNum)
    distributeDiscountToProducts(pctNum, amtNum, base)
  }

  const handleTableDiscountAmountChange = (discountAmt) => {
    const base = parseFloat(totalAmount) || 0
    const amtNum = parseFloat(discountAmt) || 0
    const pctNum = base > 0
      ? parseFloat(((amtNum / base) * 100).toFixed(4))
      : 0
    setTableDiscountAmount(discountAmt)
    setTableDiscountPercentage(pctNum)
    distributeDiscountToProducts(pctNum, amtNum, base)
  }

  // Payment mode - Cash & Card auto-calculation
  useEffect(() => {
    if (paymentMode === 'CASH_CARD' && cashAmount) {
      const cardAmt = parseFloat(netAmount) - parseFloat(cashAmount)
      setCardAmount(parseFloat(cardAmt.toFixed(2)))
    }
  }, [cashAmount, netAmount, paymentMode])

  // Transaction fee calculation based on payment mode
  useEffect(() => {
    let feePercent = 0
    let feeAmount = 0
    let baseAmount = netAmount

    if (paymentMode === 'CARD' && selectedPOS && selectedCardType) {
      // Card payment - fee on total net amount
      if (selectedCardType === 'SAME_BANK') {
        feePercent = parseFloat(selectedPOS.same_bank_transaction_fees || 0)
      } else if (selectedCardType === 'OTHER_BANK') {
        feePercent = parseFloat(selectedPOS.other_bank_transaction_fees || 0)
      }
      feeAmount = (baseAmount * feePercent) / 100
    } else if (paymentMode === 'ONLINE' && selectedMFS) {
      // Online payment - fee on total net amount
      feePercent = parseFloat(selectedMFS.transaction_charge || 0)
      feeAmount = (baseAmount * feePercent) / 100
    } else if (paymentMode === 'CASH_CARD' && selectedPOS && selectedCardType && cardAmount > 0) {
      // Cash & Card - fee only on card amount (remaining after cash)
      baseAmount = cardAmount
      if (selectedCardType === 'SAME_BANK') {
        feePercent = parseFloat(selectedPOS.same_bank_transaction_fees || 0)
      } else if (selectedCardType === 'OTHER_BANK') {
        feePercent = parseFloat(selectedPOS.other_bank_transaction_fees || 0)
      }
      feeAmount = (baseAmount * feePercent) / 100
    }

    setTransactionFeePercent(feePercent)
    setTransactionFeeAmount(parseFloat(feeAmount.toFixed(2)))
    setTotalPayable(parseFloat((netAmount + feeAmount).toFixed(2)))
  }, [paymentMode, selectedPOS, selectedCardType, selectedMFS, netAmount, cardAmount])

  // Reset payment-related states when payment mode changes
  useEffect(() => {
    if (paymentMode !== 'CARD' && paymentMode !== 'CASH_CARD') {
      setSelectedPOS(null)
      setSelectedCardType(null)
    }
    if (paymentMode !== 'ONLINE') {
      setSelectedMFS(null)
    }
    if (paymentMode !== 'CASH_CARD') {
      setCashAmount(0)
      setCardAmount(0)
    }
  }, [paymentMode])

  // Calculate weighted average VAT percentage from products
  const getWeightedVatPercentage = () => {
    if (productsInTable.length === 0 || totalAmount === 0) return 0

    let totalVatPercentageWeighted = 0
    productsInTable.forEach((p) => {
      const productAmount = p.quantity * p.ratePerUnit
      totalVatPercentageWeighted += (p.vatPercentage || 0) * productAmount
    })

    const weightedAvgVat = totalVatPercentageWeighted / totalAmount
    return parseFloat(weightedAvgVat.toFixed(2))
  }

  // Prepare invoice data
  const prepareInvoiceData = (status) => {
    return {
      customerDetails: {
        id: selectedCustomer?.id,
        name: customerName,
        mobileNumber: clampSalesLocalPhoneDigits(customerPhone),
        billingAddress: customerAddress,
        shippingAddress: customerAddress,
      },
      productDetails: productsInTable.map((p) => {
        const eff = getEffectiveLine(p)
        return {
          product_id:
            p.product_id || p.productDetails?.product_id || p.productDetails?.id,
          product_batch_inventory_id:
            p.product_batch_inventory_id ||
            p.batchDetails?.product_batch_inventory_id ||
            p.batchDetails?.id,
          quantity: p.quantity,
          discount_percentage: eff.discountPercentage,
          vat: eff.vatAmount,
          vat_percentage: eff.vatPercentage,
          rate_per_unit: p.ratePerUnit,
          final_price: eff.totalAmount,
          dose: p.dose,
          selected_batch_details: {
            rate_per_unit: p.ratePerUnit,
            cost_per_unit: p.costPerUnit,
            batch_number: p.batchNumber,
            expiry_date: p.expiryDate,
            available_quantity: availQty(p.batchDetails),
          },
        }
      }),
      paymentDetails: {
        amount: totalAmount,
        discount: tableDiscountAmount,
        vat: totalVAT,
        vat_percentage: getWeightedVatPercentage(),
        total_amount: netAmount,
        payment_mode: paymentMode,
        amount_cash: paymentMode === 'CASH_CARD' ? cashAmount : null,
        amount_card: paymentMode === 'CASH_CARD' ? cardAmount : (paymentMode === 'CARD' ? netAmount : null),
        notes: notes,
        // POS details for Card and Cash & Card payments
        pos_id: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedPOS ? selectedPOS.id : null,
        pos_account_name: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedPOS ? selectedPOS.pos_account_name : null,
        card_type: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedCardType ? selectedCardType : null,
        // MFS details for Online payments
        mfs_id: paymentMode === 'ONLINE' && selectedMFS ? selectedMFS.id : null,
        mfs_name: paymentMode === 'ONLINE' && selectedMFS ? selectedMFS.mfs_name : null,
        // Transaction fees
        transaction_fee_percent: transactionFeePercent > 0 ? transactionFeePercent : null,
        transaction_fee_amount: transactionFeeAmount > 0 ? transactionFeeAmount : null,
        // Final card amount (card amount + fee)
        final_card_amount: paymentMode === 'CARD' && transactionFeeAmount > 0 
          ? netAmount + transactionFeeAmount 
          : paymentMode === 'CASH_CARD' && cardAmount > 0 && transactionFeeAmount > 0 
            ? cardAmount + transactionFeeAmount 
            : null,
        total_payable: transactionFeeAmount > 0 ? totalPayable : netAmount,
      },
      status: status,
    }
  }

  // For NEW orders: full validation + create as COMPLETED.
  // For EDIT mode on a HOLD order: light validation, update the hold in place
  // (status stays HOLD, no cash impact, no print, navigate back to list).
  const handleSave = async () => {
    if (productsInTable.length === 0) {
      setValidationError('Please add at least one product.')
      return
    }

    const isEditingHold = isEditMode && currentOrderStatus === 'HOLD'

    // Customer details are only required to complete a sale. A held order can
    // be saved/updated without them (same as the original hold create flow).
    if (!isEditingHold && (!customerName || !customerPhone)) {
      setValidationError('Please enter customer details.')
      return
    }

    // Hold-update path: skip payment validation entirely — a held order may
    // legitimately have no paid amount yet. The user clicks Complete Sale
    // when they're ready to take payment.
    if (!isEditingHold) {
      if (returnAmount < 0) {
        setValidationError(
          'Return amount cannot be negative. Please check the paid amount.'
        )
        return
      }

      if (paymentMode === 'CASH_CARD') {
        if (!cashAmount || cashAmount <= 0) {
          setValidationError('Please enter cash amount for split payment.')
          return
        }
      } else if (paymentMode !== 'DUE_SALES') {
        if (!paidAmount || parseFloat(paidAmount) <= 0) {
          setValidationError('Please enter paid amount before saving.')
          return
        }
      }
    }

    setLoading(true)
    setServerError(undefined)

    // Customer creation/resolution is owned by the backend (hold-or-create
    // dedups by mobile and creates on the fly). Creating one here too caused a
    // duplicate customer entry, so we just send customer_details in the payload.

    // Edit-mode on a HOLD = keep it as HOLD. Otherwise complete the sale.
    const payloadStatus = isEditingHold ? 'HOLD' : 'COMPLETED'
    const data = prepareInvoiceData(payloadStatus)

    const savePromise = isEditMode
      ? updateSalesHelper({ ...data, id: id })
      : createSalesHelper(data)

    savePromise
      .then(async (result) => {
        dispatch(
          isEditMode
            ? updateSalesAlertAction(true)
            : createSalesAlertAction(true)
        )
        setTimeout(() => {
          dispatch(
            isEditMode
              ? updateSalesAlertAction(false)
              : createSalesAlertAction(false)
          )
        }, 5000)

        // Print invoice only when the sale is actually completed (not on
        // a hold-update).
        if (!isEditingHold && shouldPrint && result?.id) {
          try {
            await handlePrintInvoice(result.id, loginData?.name, loginData?.pharmacy_name)
            console.log('Invoice printed successfully')
          } catch (err) {
            console.error('Print error:', err)
          }
        }

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to save sales order')
        setLoading(false)
      })
  }

  // Explicitly transition a HOLD order to COMPLETED. Runs the full payment
  // validation block, then updates the existing order with status=COMPLETED
  // so the backend ticks cash counters and prints the receipt.
  const handleCompleteSale = async () => {
    if (productsInTable.length === 0) {
      setValidationError('Please add at least one product.')
      return
    }

    if (!customerName || !customerPhone) {
      setValidationError('Please enter customer details.')
      return
    }

    if (returnAmount < 0) {
      setValidationError(
        'Return amount cannot be negative. Please check the paid amount.'
      )
      return
    }

    if (paymentMode === 'CASH_CARD') {
      if (!cashAmount || cashAmount <= 0) {
        setValidationError('Please enter cash amount for split payment.')
        return
      }
    } else if (paymentMode !== 'DUE_SALES') {
      if (!paidAmount || parseFloat(paidAmount) <= 0) {
        setValidationError('Please enter paid amount before completing the sale.')
        return
      }
    }

    setLoading(true)
    setServerError(undefined)

    // Customer creation/resolution is handled server-side; no client-side
    // create here (it caused duplicate customer entries).

    const data = prepareInvoiceData('COMPLETED')

    updateSalesHelper({ ...data, id: id })
      .then(async (result) => {
        dispatch(createSalesAlertAction(true))
        setTimeout(() => {
          dispatch(createSalesAlertAction(false))
        }, 5000)

        if (shouldPrint && result?.id) {
          try {
            await handlePrintInvoice(result.id, loginData?.name, loginData?.pharmacy_name)
          } catch (err) {
            console.error('Print error:', err)
          }
        }

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to complete sale')
        setLoading(false)
      })
  }

  const handleHold = async () => {
    if (productsInTable.length === 0) {
      setServerError('Please add at least one product.')
      return
    }

    setLoading(true)
    setServerError(undefined)

    // Customer creation/resolution is handled server-side; no client-side
    // create here (it caused duplicate customer entries).

    const data = prepareInvoiceData('HOLD')

    createSalesHelper(data)
      .then(() => {
        dispatch(holdSalesAlertAction(true))
        setTimeout(() => {
          dispatch(holdSalesAlertAction(false))
        }, 5000)

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setServerError(err || 'Failed to hold sales order')
        setLoading(false)
      })
  }

  const handleClose = () => {
    if (productsInTable.length > 0 && !isEditMode) {
      setShowCloseConfirmation(true)
    } else {
      clearDraft(DRAFT_KEY)
      navigate('/home/details')
    }
  }

  const handleConfirmClose = () => {
    clearDraft(DRAFT_KEY)
    navigate('/home/details')
  }

  const handleCancelClose = () => {
    setShowCloseConfirmation(false)
  }

  const handleCancelSale = () => {
    setShowCancelConfirmation(true)
  }

  const handleConfirmCancelSale = async () => {
    setLoading(true)
    setShowCancelConfirmation(false)
    setServerError(undefined)

    cancelSalesHelper(id)
      .then(() => {
        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setServerError(err || 'Failed to cancel sales order')
        setLoading(false)
      })
  }

  const handleCancelCancelSale = () => {
    setShowCancelConfirmation(false)
  }

  // Online Order Handlers
  const handleConfirmOnlineOrder = () => {
    if (productsInTable.length === 0) {
      setValidationError('Please add at least one product before confirming.')
      return
    }
    setShowConfirmPopup(true)
  }

  const handleConfirmOnlineOrderAction = async () => {
    setLoading(true)
    setShowConfirmPopup(false)
    setServerError(undefined)

    // For prescription orders, update products first
    if (isPrescriptionOrder && onlineOrderStatus === 'PRESCRIPTION_PENDING_ON_STORE') {
      const updateData = {
        productDetails: productsInTable.map(product => {
          const eff = getEffectiveLine(product)
          return {
            product_batch_inventory_id: product.product_batch_inventory_id ||
              product.batchDetails?.product_batch_inventory_id ||
              product.batchDetails?.id ||
              product.productDetails?.inventory?.id,
            quantity: product.quantity,
            discount_percentage: eff.discountPercentage,
            vat: eff.vatPercentage,
            selected_batch_details: {
              rate_per_unit: product.ratePerUnit,
              cost_per_unit: product.costPerUnit,
              batch_number: product.batchNumber,
              expiry_date: product.expiryDate,
            },
          }
        }),
        paymentDetails: {
          payment_mode: paymentMode,
          discount: tableDiscountPercentage,
          amount: totalAmount,
          total_amount: netAmount,
          vat: totalVAT,
        },
        orderId: id,
      }

      updatePrescriptionOrderHelper(updateData)
        .then(() => {
          // After updating, confirm the order
          confirmOnlineOrder()
        })
        .catch((err) => {
          setValidationError(err || 'Failed to update prescription order')
          setLoading(false)
        })
    } else {
      // For normal online orders, just confirm
      confirmOnlineOrder()
    }
  }

  const confirmOnlineOrder = () => {
    const data = {
      id: id,
      deliveryPartnerId: null, // Will use third-party delivery
      status: SALES_TYPE.CONFIRMED,
    }

    confirmOnlineSalesOrderHelper(data)
      .then(() => {
        dispatch(updateSalesAlertAction(true))
        setTimeout(() => {
          dispatch(updateSalesAlertAction(false))
        }, 5000)

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to confirm online order')
        setLoading(false)
      })
  }

  const handleCancelOnlineOrder = () => {
    setShowCancelOrderPopup(true)
  }

  const handleConfirmCancelOnlineOrder = async () => {
    setLoading(true)
    setShowCancelOrderPopup(false)
    setServerError(undefined)

    const data = {
      id: id,
      deliveryPartnerId: null,
      status: SALES_TYPE.CANCELLED,
    }

    confirmOnlineSalesOrderHelper(data)
      .then(() => {
        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to cancel online order')
        setLoading(false)
      })
  }

  const handleCancelCancelOnlineOrder = () => {
    setShowCancelOrderPopup(false)
  }

  const customerNameSelectValue = useMemo(() => {
    if (selectedCustomer?.id) {
      const fromList = branchCustomerOptions.find(
        (o) => o.value === selectedCustomer.id
      )
      if (fromList) return fromList
      return {
        value: selectedCustomer.id,
        label: customerName || selectedCustomer.name || '',
        id: selectedCustomer.id,
        name: selectedCustomer.name || customerName,
        mobile_no: selectedCustomer.mobile_no,
        address:
          selectedCustomer.address ||
          selectedCustomer.billing_address ||
          selectedCustomer.shipping_address ||
          '',
        billing_address: selectedCustomer.billing_address,
        shipping_address: selectedCustomer.shipping_address,
      }
    }
    if (customerName && String(customerName).trim() !== '') {
      return {
        value: null,
        label: customerName.trim(),
        __isNew__: true,
      }
    }
    return null
  }, [
    selectedCustomer?.id,
    selectedCustomer?.name,
    selectedCustomer?.mobile_no,
    selectedCustomer?.billing_address,
    selectedCustomer?.shipping_address,
    selectedCustomer?.address,
    customerName,
    branchCustomerOptions,
  ])

  const batchOptions = useMemo(() => {
    const inventory = selectedProduct?.inventory
    if (!Array.isArray(inventory) || inventory.length === 0) return []
    // Show only batches with available quantity (after deducting holds)
    return inventory
      .filter((b) => availQty(b) > 0)
      .map((b) => ({
        value: b.product_batch_inventory_id || b.id,
        label: `${b.batch_number}, Exp: ${formatDate(b.expiry_date)}, Avail: ${availQty(b)}`,
        batchData: b,
      }))
  }, [selectedProduct?.inventory])

  const selectedBatchOption = useMemo(() => {
    if (!selectedBatch) return null
    const id = selectedBatch.product_batch_inventory_id || selectedBatch.id
    return batchOptions.find((o) => o.value === id) || {
      value: id,
      label: `${selectedBatch.batch_number}, Exp: ${formatDate(selectedBatch.expiry_date)}, Avail: ${availQty(selectedBatch)}`,
      batchData: selectedBatch,
    }
  }, [selectedBatch, batchOptions])

  return (
    <div className="containerHome">
      {!isMobile && <Header />}
      <SideMenu activeItem={SIDEBAR_CONST.HOME} component="HOME_DETAILS" />
      {isMobile && (
        <MobileHeader
          handleBack={handleClose}
          title={isEditMode ? 'Edit Sales' : 'New Sales'}
        />
      )}
      <div className="pageBody dFlex">
        {!isMobile && (
          <div className="pageBodyLeftPanel sticky">
            <div className="chainNavigationWrapper">
              <ul className="chainNavigation">
                {HOME_NAVIGATION_ITEMS.map((item) => {
                  const feature = permissions?.find(
                    (p) => p.feature === item.feature
                  )
                  if (feature?.type !== PERMISSION_TYPE.RESTRICTED) {
                    return (
                      <li
                        key={item.name}
                        className={`chainItem ${
                          item.name === HOME_CONST.SALES ? 'active' : ''
                        }`}
                        onClick={() => {
                          dispatch(activeHomeDetailNavigation(item.name))
                          navigate('/home/details')
                        }}
                      >
                        {item.homeDetailIcon}
                        <span>{item.name}</span>
                      </li>
                    )
                  }
                  return null
                })}
              </ul>
            </div>
          </div>
        )}
        <div className="pageBodyRightPanel sales-form-container">
          <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter mb-4">
            <div className="pageBodyRightPanelHeading">
              {isEditMode ? 'Edit Sales' : 'New Sales'}
            </div>
            <button className="btn btnSecondary" onClick={handleClose}>
              Close
            </button>
          </div>

          <form
            onSubmit={(e) => e.preventDefault()}
            onKeyDown={handleSalesFormKeyDown}
          >
            {/* Online Order Info Banner */}
            {isOnlineOrder && (
              <div className="online-order-banner">
                <div className="banner-content">
                  <span className="banner-badge">Online Order</span>
                  <span className="banner-status">
                    Status: {onlineOrderStatus === 'PRESCRIPTION_PENDING_ON_STORE' 
                      ? 'Prescription Pending' 
                      : onlineOrderStatus === 'CREATED' 
                        ? 'Awaiting Confirmation' 
                        : onlineOrderStatus}
                  </span>
                  {isPrescriptionOrder && (
                    <span className="banner-type">Prescription Order</span>
                  )}
                </div>
              </div>
            )}

            {/* Prescription Images Section */}
            {isOnlineOrder && isPrescriptionOrder && prescriptionFiles.length > 0 && (
              <div className="sales-form-section prescription-section">
                <h3 className="section-title">Prescription Images</h3>
                <div className="prescription-images-grid">
                  {prescriptionFiles.map((file, index) => (
                    <div key={index} className="prescription-image-item">
                      <div 
                        className="prescription-thumbnail"
                        onClick={() => setSelectedPrescriptionImage(file)}
                      >
                        <img 
                          src={Config.getFullImageUrl(file.url)} 
                          alt={file.name || `Prescription ${index + 1}`}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                        <div className="prescription-placeholder" style={{ display: 'none' }}>
                          Click to view
                        </div>
                      </div>
                      <span className="prescription-label">{file.name || `Prescription ${index + 1}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 1: Customer & Product Details */}
            <div className="sales-form-section">
              <h3 className="section-title">Customer Details</h3>
              <div className="sales-customer-details">
              <div className="form-grid">
                <div className="form-field">
                  <label>Phone</label>
                  <div className="contact-input">
                    <input
                      type="text"
                      ref={customerCountryCodeRef}
                      value={customerCountryCode}
                      maxLength={SALES_COUNTRY_CODE_MAX_LEN}
                      disabled={true}
                      onChange={(e) =>
                        setCustomerCountryCode(
                          clampSalesCountryCode(e.target.value)
                        )
                      }
                      onKeyDown={(e) =>
                        handleFieldKeyDown(e, [
                          customerPhoneRef,
                          customerNameSelectWrapRef,
                          customerAddressRef,
                          barcodeInputRef,
                        ])
                      }
                      placeholder="+88"
                    />
                    <div className="customer-field-wrapper">
                      <input
                        type="text"
                        ref={customerPhoneRef}
                        placeholder="Enter phone"
                        value={customerPhone}
                        maxLength={SALES_LOCAL_PHONE_MAX_LEN}
                        inputMode="numeric"
                        disabled={isCustomerDetailsLocked}
                        onChange={(e) =>
                          handleCustomerPhoneChange(e.target.value)
                        }
                        onKeyDown={handleCustomerPhoneKeyDown}
                        onFocus={() => {
                          if (customerSuggestions.length > 0)
                            setShowCustomerSuggestions(true)
                        }}
                      />
                      {showCustomerSuggestions &&
                        customerSuggestions.length > 0 && (
                          <div className="customer-suggestions">
                            {customerSuggestions.map((customer, index) => (
                              <div
                                key={customer.id || index}
                                className={`customer-suggestion-item ${
                                  index === selectedSuggestionIndex
                                    ? 'highlighted'
                                    : ''
                                }`}
                                onClick={() => handleSelectCustomer(customer)}
                                onMouseEnter={() =>
                                  setSelectedSuggestionIndex(index)
                                }
                              >
                                <div className="customer-name">
                                  {customer.name}
                                </div>
                                <div className="customer-phone">
                                  {customer.mobile_no}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                <div className="form-field">
                  <label>Name</label>
                  <div className="customer-field-wrapper">
                    <div
                      ref={customerNameSelectWrapRef}
                      className={`sales-customer-name-wrap${
                        isCustomerDetailsLocked
                          ? ' sales-customer-name-selected'
                          : ''
                      }`}
                    >
                      <CreatableSelect
                        instanceId="sales-customer-name"
                        classNamePrefix="react-select"
                        className="reactSelect-compact reactSelect-customer-name"
                        isClearable
                        isSearchable
                        openMenuOnClick
                        openMenuOnFocus
                        options={branchCustomerOptions}
                        value={customerNameSelectValue}
                        onChange={handleCustomerNameSelectChange}
                        onMenuOpen={() => {
                          customerNameSelectMenuOpenRef.current = true
                        }}
                        onMenuClose={() => {
                          customerNameSelectMenuOpenRef.current = false
                        }}
                        onKeyDown={(e) =>
                          handleSelectKeyDown(
                            e,
                            [customerAddressRef, barcodeInputRef],
                            customerNameSelectMenuOpenRef.current
                          )
                        }
                        placeholder="Select or enter name"
                        menuPortalTarget={document.body}
                        filterOption={(option, rawInput) => {
                          const q = String(rawInput || '').toLowerCase()
                          if (!q) return true
                          const d = option.data || option
                          const label = String(d.label || '').toLowerCase()
                          const mob = String(d.mobile_no || '').toLowerCase()
                          return label.includes(q) || mob.includes(q)
                        }}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          placeholder: (base) => ({
                            ...base,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }),
                          singleValue: (base) => ({
                            ...base,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }),
                          control: (base) => ({
                            ...base,
                            minWidth: 0,
                            width: '100%',
                            minHeight: '36px',
                            maxHeight: '36px',
                            borderColor: '#d4d4d4',
                            alignItems: 'center',
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: '2px 8px',
                            maxHeight: '32px',
                            overflow: 'hidden',
                            alignItems: 'center',
                          }),
                          input: (base) => ({
                            ...base,
                            margin: 0,
                            padding: 0,
                          }),
                          clearIndicator: (base) => ({
                            ...base,
                            padding: '6px 4px',
                            cursor: 'pointer',
                          }),
                          dropdownIndicator: (base) => ({
                            ...base,
                            padding: '6px 4px',
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: '13px',
                            padding: '8px 12px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }),
                          menu: (base) => ({
                            ...base,
                            width: 'max-content',
                            minWidth: '100%',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-field">
                  <label>Address</label>
                  <input
                    type="text"
                    ref={customerAddressRef}
                    placeholder="Enter address"
                    value={customerAddress}
                    disabled={isCustomerDetailsLocked}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    onKeyDown={(e) => handleFieldKeyDown(e, [barcodeInputRef])}
                  />
                </div>
              </div>
              </div>

              <h3 className="section-title sales-product-details-title">
                Product Details
              </h3>
              <div className="sales-product-details">
              <div className="form-grid">
                <div className="form-field with-icon">
                  <label>Barcode/QR</label>
                  <input
                    type="text"
                    placeholder="Scan or enter barcode"
                    value={productBarcode}
                    ref={barcodeInputRef}
                    onChange={(e) => setProductBarcode(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    onBlur={() => getProductDetailsByBarcode(productBarcode)}
                  />
                  <div
                    className="field-icon"
                    onClick={() => setIsScannerVisible(!isScannerVisible)}
                  >
                    <BarcodeIcon />
                  </div>
                  {isScannerVisible && <BarcodeScanner onScan={handleScan} />}
                </div>

                <div className="form-field">
                  <label>Product</label>
                  <div
                    ref={productSelectWrapRef}
                    className="sales-select-field-wrap"
                  >
                    <Select
                      instanceId={SALES_PRODUCT_SELECT_INSTANCE_ID}
                      classNamePrefix="react-select"
                      className="reactSelect-compact reactSelect-product"
                      value={
                        selectedProduct
                          ? {
                              label: selectedProduct.details,
                              value: selectedProduct.id,
                              id: selectedProduct.id,
                            }
                          : null
                      }
                      onInputChange={handleProductSearch}
                      onChange={(option) => {
                        productSelectMenuOpenRef.current = false
                        handleProductSelect(option)
                      }}
                      onMenuOpen={() => {
                        productSelectMenuOpenRef.current = true
                        if (productSearchSuggestions.length === 0) {
                          setProductSearchSuggestions(allProducts)
                        }
                      }}
                      onMenuClose={() => {
                        productSelectMenuOpenRef.current = false
                      }}
                      options={productSearchSuggestions.map((p) => ({
                        label: formatProductListLabel(p),
                        value: p.id,
                        id: p.id,
                      }))}
                      placeholder="Select..."
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          minWidth: 0,
                          width: '100%',
                          minHeight: '36px',
                          maxHeight: '36px',
                          borderColor: '#d4d4d4',
                          alignItems: 'center',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '2px 8px',
                          maxHeight: '32px',
                          overflow: 'hidden',
                          alignItems: 'center',
                        }),
                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        option: (base) => ({
                          ...base,
                          fontSize: '13px',
                          padding: '8px 12px',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }),
                        menu: (base) => ({
                          ...base,
                          width: 'max-content',
                          minWidth: '100%',
                        }),
                      }}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Batch</label>
                  <div
                    ref={batchSelectWrapRef}
                    className="sales-select-field-wrap"
                  >
                    <Select
                      options={batchOptions}
                      value={selectedBatchOption}
                      onChange={(option) => {
                        setSelectedBatch(option?.batchData || null)
                      }}
                      onMenuOpen={() => { batchSelectMenuOpenRef.current = true }}
                      onMenuClose={() => { batchSelectMenuOpenRef.current = false }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !batchSelectMenuOpenRef.current) {
                          e.preventDefault()
                          quantityRef.current?.focus()
                        }
                      }}
                      placeholder={selectedProduct ? 'No batch available' : 'Select a product first'}
                      isDisabled={!selectedProduct}
                      isClearable={false}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          minHeight: '36px',
                          maxHeight: '36px',
                          fontSize: '13px',
                        }),
                      }}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Quantity</label>
                  <input
                    type="number"
                    ref={quantityRef}
                    placeholder="Qty"
                    value={numInput(quantity)}
                    onChange={(e) => setQuantity(stripLeadingZeros(e.target.value))}
                    onKeyDown={(e) =>
                      handleFieldKeyDown(e, discountPercentageRef)
                    }
                  />
                  {selectedBatch && (
                    <span className="available-stock">
                      Avail: {availQty(selectedBatch)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-grid-6 mt-2">
                <div className="form-field">
                  <label>CPU</label>
                  <input
                    type="number"
                    value={numInput(costPerUnit)}
                    onChange={(e) => setCostPerUnit(stripLeadingZeros(e.target.value))}
                    disabled
                  />
                </div>

                <div className="form-field">
                  <label>Disc %</label>
                  <input
                    type="number"
                    ref={discountPercentageRef}
                    value={numInput(discountPercentage)}
                    onChange={(e) => {
                      const cleaned = stripLeadingZeros(e.target.value)
                      setDiscountPercentage(cleaned)
                      setLastDiscountField('percentage')
                      // Forward-compute Disc Amt so the two fields stay in sync.
                      const qtyNum = parseFloat(quantity) || 0
                      const rateNum = parseFloat(ratePerUnit) || 0
                      const base = qtyNum * rateNum
                      const pctNum = parseFloat(cleaned) || 0
                      if (base > 0) {
                        setDiscountAmount(parseFloat(((base * pctNum) / 100).toFixed(2)))
                      } else {
                        setDiscountAmount(0)
                      }
                    }}
                    onKeyDown={(e) =>
                      handleFieldKeyDown(e, discountAmountRef)
                    }
                  />
                </div>

                <div className="form-field">
                  <label>Disc Amt</label>
                  <input
                    type="number"
                    ref={discountAmountRef}
                    value={numInput(discountAmount)}
                    onChange={(e) => {
                      const cleaned = stripLeadingZeros(e.target.value)
                      setDiscountAmount(cleaned)
                      setLastDiscountField('amount')
                      // Back-compute Disc % so the two fields stay in sync.
                      const qtyNum = parseFloat(quantity) || 0
                      const rateNum = parseFloat(ratePerUnit) || 0
                      const base = qtyNum * rateNum
                      const amtNum = parseFloat(cleaned) || 0
                      if (base > 0) {
                        const pct = (amtNum / base) * 100
                        setDiscountPercentage(parseFloat(pct.toFixed(4)))
                      } else {
                        setDiscountPercentage(0)
                      }
                    }}
                    onKeyDown={handleProductLastFieldKeyDown}
                  />
                </div>

                <div className="form-field">
                  <label>VAT %</label>
                  <input
                    type="number"
                    value={numInput(vatPercentage)}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-field">
                  <label>VAT Amt</label>
                  <input
                    type="number"
                    value={numInput(vatAmount)}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-field">
                  <label>RPU</label>
                  <input
                    type="number"
                    value={numInput(ratePerUnit)}
                    disabled
                    className="disabled-input"
                  />
                </div>
              </div>

              <div className="form-actions-right mt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (handleAddOrUpdateProduct()) {
                      setTimeout(() => barcodeInputRef.current?.focus(), 0)
                    }
                  }}
                  className="btn btn-primary"
                >
                  {editingRowIndex !== null ? 'UPDATE' : 'ADD'}
                </button>
                <button
                  type="button"
                  onClick={handleClearProduct}
                  className="btn btn-secondary"
                >
                  CLEAR
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteProductPopup(true)}
                  className="btn btn-danger"
                  disabled={editingRowIndex === null}
                >
                  DELETE
                </button>
                    <label
                  className="print-checkbox-tooltip"
                  title="Print after save"
                >
                  <input
                    type="checkbox"
                    checked={shouldPrint}
                    onChange={(e) => setShouldPrint(e.target.checked)}
                  />
                </label>
              </div>
              </div>

              {serverError && (
                <div className="invalidFeedback mt-3">{serverError}</div>
              )}
            </div>

            {/* Section 3: Products Table with Bill Summary */}
            <div className="sales-form-section mt-3">
              <div className="product-list-with-summary">
                <div className="product-list-section">
                  <h3 className="section-title">Product List</h3>
                  {productsInTable.length === 0 ? (
                    <p className="empty-message">
                      No products added yet. Add products using the form above.
                    </p>
                  ) : (
                    <div className="products-table-wrapper">
                      <table className="products-table">
                        <thead>
                          <tr>
                            <th>SL</th>
                            <th>Product Name (Size)-Strength</th>
                            <th>Category</th>
                            <th>Batch</th>
                            <th>Expiry</th>
                            <th>Qty</th>
                            <th>VAT %</th>
                            <th>VAT Amt</th>
                            <th>Disc %</th>
                            <th>Total Disc</th>
                            <th>RPU</th>
                            <th>Amount</th>
                            <th>Rx Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productsInTable.map((product, index) => {
                            const eff = getEffectiveLine(product)
                            return (
                            <tr
                              key={product.id}
                              onClick={() => handleTableRowClick(index)}
                              onDoubleClick={() =>
                                handleTableRowDoubleClick(index)
                              }
                              className={
                                selectedTableRowIndex === index
                                  ? 'selected'
                                  : ''
                              }
                              title="Double-click to edit"
                            >
                              <td>{index + 1}</td>
                              <td>{formatProductListLabel(
                                {
                                  name: product.productName,
                                  productName: product.productName,
                                  size: product.size,
                                  dose: product.dose,
                                  strength: product.strength,
                                  category: product.category,
                                },
                                { includeCategory: false }
                              )}</td>
                              <td>{product.category || '-'}</td>
                              <td>{product.batchNumber}</td>
                              <td>{formatDate(product.expiryDate)}</td>
                              <td>{product.quantity}</td>
                              <td>{eff.vatPercentage}</td>
                              <td>{eff.vatAmount.toFixed(2)}</td>
                              <td>{eff.discountPercentage}</td>
                              <td>{eff.discountAmount.toFixed(2)}</td>
                              <td>{product.ratePerUnit?.toFixed(2)}</td>
                              <td>{eff.totalAmount.toFixed(2)}</td>
                              <td>{product.rxMode || 'OTC'}</td>
                            </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Bill Summary */}
                <div className="bill-summary-compact">
                  <h3 className="section-title">Bill Summary</h3>
                  <div className="summary-header-grid">
                    <div className="summary-box">
                      <div className="summary-box-label">Total Qty</div>
                      <div className="summary-box-value">{totalQty}</div>
                    </div>
                    <div className="summary-box">
                      <div className="summary-box-label">Total Items</div>
                      <div className="summary-box-value">{totalItems}</div>
                    </div>
                  </div>
                  <table className="summary-table">
                    <tbody>
                      <tr>
                        <td>Total Amt</td>
                        <td className="text-right">{totalAmount.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>Discount %</td>
                        <td className="text-right">
                          <input
                            type="number"
                            ref={tableDiscountPercentageRef}
                            value={numInput(tableDiscountPercentage)}
                            onChange={(e) =>
                              handleTableDiscountPercentageChange(
                                parseFloat(stripLeadingZeros(e.target.value) || 0)
                              )
                            }
                            onKeyDown={(e) =>
                              handleFieldKeyDown(e, [tableDiscountAmountRef])
                            }
                            className="summary-input"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Disc Amt</td>
                        <td className="text-right">
                          <input
                            type="number"
                            ref={tableDiscountAmountRef}
                            value={numInput(tableDiscountAmount)}
                            onChange={(e) =>
                              handleTableDiscountAmountChange(
                                parseFloat(stripLeadingZeros(e.target.value) || 0)
                              )
                            }
                            onKeyDown={(e) =>
                              handleFieldKeyDown(e, [paidAmountRef])
                            }
                            className="summary-input"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Sub Total</td>
                        <td className="text-right">{subTotal.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>VAT</td>
                        <td className="text-right">{totalVAT.toFixed(2)}</td>
                      </tr>
                      <tr className="highlight-row">
                        <td className="highlight">Net Amt</td>
                        <td className="text-right highlight">
                          {netAmount.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td>Pay Mode</td>
                        <td>
                          <Select
                            className="summary-select"
                            value={PAYMENT_MODE_OPTIONS.find(
                              (opt) => opt.name === paymentMode
                            )}
                            onChange={(option) => setPaymentMode(option.name)}
                            options={PAYMENT_MODE_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.name}
                            // TEMP-DISABLE-PAYMODE: remove this prop to re-enable.
                            isOptionDisabled={(option) =>
                              TEMP_DISABLED_PAY_MODES.includes(option.name)
                            }
                            placeholder="Select..."
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({
                                ...base,
                                minHeight: '32px',
                                fontSize: '12px',
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                              }),
                              // TEMP-DISABLE-PAYMODE: grey out disabled options. Remove this block to revert.
                              option: (base, state) => ({
                                ...base,
                                ...(state.isDisabled && {
                                  color: '#bbb',
                                  cursor: 'not-allowed',
                                }),
                              }),
                            }}
                          />
                        </td>
                      </tr>
                      {/* Card Payment - Select Bank and Card Type */}
                      {paymentMode === 'CARD' && (
                        <>
                          <tr>
                            <td>Select Bank</td>
                            <td>
                              {posList.length > 0 ? (
                                <Select
                                  className="summary-select"
                                  value={selectedPOS}
                                  onChange={(option) => {
                                    setSelectedPOS(option)
                                    setSelectedCardType(null) // Reset card type when bank changes
                                  }}
                                  options={posList}
                                  getOptionLabel={(option) => option.pos_account_name}
                                  getOptionValue={(option) => option.id}
                                  placeholder="Select Bank..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                  No POS configured. Add in Settings.
                                </span>
                              )}
                            </td>
                          </tr>
                          {selectedPOS && (
                            <tr>
                              <td>Card Type</td>
                              <td>
                                <Select
                                  className="summary-select"
                                  value={CARD_TYPE_OPTIONS.find(opt => opt.name === selectedCardType)}
                                  onChange={(option) => setSelectedCardType(option?.name || null)}
                                  options={CARD_TYPE_OPTIONS}
                                  getOptionLabel={(option) => option.label}
                                  getOptionValue={(option) => option.name}
                                  placeholder="Select Type..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                          {selectedPOS && selectedCardType && transactionFeePercent > 0 && (
                            <>
                              <tr>
                                <td>Fee ({transactionFeePercent}%)</td>
                                <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td>Final Card Amt</td>
                                <td className="text-right">{(netAmount + transactionFeeAmount).toFixed(2)}</td>
                              </tr>
                            </>
                          )}
                        </>
                      )}
                      {/* Online Payment - Select MFS */}
                      {paymentMode === 'ONLINE' && (
                        <>
                          <tr>
                            <td>Select Type</td>
                            <td>
                              {mfsList.length > 0 ? (
                                <Select
                                  className="summary-select"
                                  value={selectedMFS}
                                  onChange={(option) => setSelectedMFS(option)}
                                  options={mfsList}
                                  getOptionLabel={(option) => option.mfs_name}
                                  getOptionValue={(option) => option.id}
                                  placeholder="Select MFS..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                  No MFS configured. Add in Settings.
                                </span>
                              )}
                            </td>
                          </tr>
                          {selectedMFS && transactionFeePercent > 0 && (
                            <tr>
                              <td>Fee ({transactionFeePercent}%)</td>
                              <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                            </tr>
                          )}
                        </>
                      )}
                      {/* Cash & Card Payment */}
                      {paymentMode === 'CASH_CARD' && (
                        <>
                          <tr>
                            <td>Cash Amt</td>
                            <td>
                              <input
                                type="number"
                                value={numInput(cashAmount)}
                                onChange={(e) =>
                                  setCashAmount(parseFloat(stripLeadingZeros(e.target.value) || 0))
                                }
                                onKeyDown={(e) =>
                                  handleFieldKeyDown(e, [paidAmountRef])
                                }
                                className="summary-input"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>Card Amt</td>
                            <td className="text-right">
                              {cardAmount.toFixed(2)}
                            </td>
                          </tr>
                          {cardAmount > 0 && (
                            <>
                              <tr>
                                <td>Select Bank</td>
                                <td>
                                  {posList.length > 0 ? (
                                    <Select
                                      className="summary-select"
                                      value={selectedPOS}
                                      onChange={(option) => {
                                        setSelectedPOS(option)
                                        setSelectedCardType(null)
                                      }}
                                      options={posList}
                                      getOptionLabel={(option) => option.pos_account_name}
                                      getOptionValue={(option) => option.id}
                                      placeholder="Select Bank..."
                                      isClearable
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                          ...base,
                                          minHeight: '32px',
                                          fontSize: '12px',
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          padding: '0 6px',
                                        }),
                                      }}
                                    />
                                  ) : (
                                    <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                      No POS configured. Add in Settings.
                                    </span>
                                  )}
                                </td>
                              </tr>
                              {selectedPOS && (
                                <tr>
                                  <td>Card Type</td>
                                  <td>
                                    <Select
                                      className="summary-select"
                                      value={CARD_TYPE_OPTIONS.find(opt => opt.name === selectedCardType)}
                                      onChange={(option) => setSelectedCardType(option?.name || null)}
                                      options={CARD_TYPE_OPTIONS}
                                      getOptionLabel={(option) => option.label}
                                      getOptionValue={(option) => option.name}
                                      placeholder="Select Type..."
                                      isClearable
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                          ...base,
                                          minHeight: '32px',
                                          fontSize: '12px',
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          padding: '0 6px',
                                        }),
                                      }}
                                    />
                                  </td>
                                </tr>
                              )}
                              {selectedPOS && selectedCardType && transactionFeePercent > 0 && (
                                <>
                                  <tr>
                                    <td>Fee ({transactionFeePercent}%)</td>
                                    <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td>Final Card Amt</td>
                                    <td className="text-right">{(cardAmount + transactionFeeAmount).toFixed(2)}</td>
                                  </tr>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {/* Total Payable with fees */}
                      {(paymentMode === 'CARD' || paymentMode === 'ONLINE' || paymentMode === 'CASH_CARD') && transactionFeeAmount > 0 && (
                        <tr className="highlight-row">
                          <td className="highlight">Total Payable</td>
                          <td className="text-right highlight">
                            {totalPayable.toFixed(2)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>Paid Amt</td>
                        <td>
                          <input
                            type="number"
                            ref={paidAmountRef}
                            value={numInput(paidAmount)}
                            onChange={(e) => {
                              const cleaned = stripLeadingZeros(e.target.value)
                              setPaidAmount(cleaned ? parseFloat(cleaned) : '')
                            }}
                            onKeyDown={(e) => handleFieldKeyDown(e, null)}
                            className="summary-input"
                            placeholder=""
                          />
                        </td>
                      </tr>
                      <tr className="total-row">
                        <td>Return Amt</td>
                        <td className="text-right highlight">
                          {returnAmount.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Show different button based on order type */}
                  {isOnlineOrder ? (
                    <button
                      type="button"
                      onClick={handleConfirmOnlineOrder}
                      className="btn btn-primary btn-block mt-3"
                      disabled={loading}
                    >
                      {loading ? 'Confirming...' : 'Confirm Order'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSave}
                      className="btn btn-primary btn-block mt-3"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions-bottom">
              {/* For in-store orders */}
              {!isOnlineOrder && !isEditMode && (
                <button
                  type="button"
                  onClick={handleHold}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Hold Invoice'}
                </button>
              )}
              {!isOnlineOrder && isEditMode && currentOrderStatus === 'HOLD' && (
                <button
                  type="button"
                  onClick={handleCompleteSale}
                  className="btn btn-success"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Complete Sale'}
                </button>
              )}
              {!isOnlineOrder && isEditMode && (
                <button
                  type="button"
                  onClick={handleCancelSale}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Cancel Sale
                </button>
              )}
              {/* For online orders */}
              {isOnlineOrder && (
                <button
                  type="button"
                  onClick={handleCancelOnlineOrder}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Cancel Order
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirmation && (
        <PopupCard
          header="End Sales Process"
          body="Are you sure you want to end the sales process? Any unsaved data will be lost."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelClose}
          >
            Cancel
          </button>
          <button className="btn btnDanger" onClick={handleConfirmClose}>
            End Process
          </button>
        </PopupCard>
      )}

      {/* Delete Product Confirmation */}
      {deleteProductPopup && (
        <PopupCard
          header="Delete Product"
          body="Are you sure you want to delete this product from the list?"
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={() => setDeleteProductPopup(false)}
          >
            Cancel
          </button>
          <button className="btn btnDanger" onClick={handleDeleteProduct}>
            Delete
          </button>
        </PopupCard>
      )}

      {/* Cancel Sale Confirmation */}
      {showCancelConfirmation && (
        <PopupCard
          header="Cancel Sale"
          body="Are you sure you want to cancel this sale? This action cannot be undone."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelCancelSale}
          >
            No, Keep Sale
          </button>
          <button className="btn btnDanger" onClick={handleConfirmCancelSale}>
            Yes, Cancel Sale
          </button>
        </PopupCard>
      )}

      {/* Validation Error Popup */}
      {validationError && (
        <PopupCard header="Validation Error" body={validationError}>
          <button
            className="btn btnPrimary"
            onClick={() => setValidationError(null)}
          >
            OK
          </button>
        </PopupCard>
      )}

      {/* Confirm Online Order Popup */}
      {showConfirmPopup && (
        <PopupCard
          header="Confirm Online Order"
          body="Are you sure you want to confirm this order? The order will be sent for delivery."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={() => setShowConfirmPopup(false)}
          >
            Cancel
          </button>
          <button className="btn btnPrimary" onClick={handleConfirmOnlineOrderAction}>
            Confirm Order
          </button>
        </PopupCard>
      )}

      {/* Cancel Online Order Popup */}
      {showCancelOrderPopup && (
        <PopupCard
          header="Cancel Online Order"
          body="Are you sure you want to cancel this online order? The customer will be notified."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelCancelOnlineOrder}
          >
            No, Keep Order
          </button>
          <button className="btn btnDanger" onClick={handleConfirmCancelOnlineOrder}>
            Yes, Cancel Order
          </button>
        </PopupCard>
      )}

      {/* Prescription Image Zoom Popup */}
      {selectedPrescriptionImage && (
        <div className="prescription-modal-overlay" onClick={() => setSelectedPrescriptionImage(null)}>
          <div className="prescription-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="prescription-modal-close"
              onClick={() => setSelectedPrescriptionImage(null)}
            >
              ✕
            </button>
            <div className="prescription-modal-content">
              <img 
                src={Config.getFullImageUrl(selectedPrescriptionImage.url)} 
                alt={selectedPrescriptionImage.name || 'Prescription'}
              />
            </div>
            <div className="prescription-modal-footer">
              <span>{selectedPrescriptionImage.name || 'Prescription Image'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesForm
